import * as _ from 'lodash-es';
import React from 'react';
import { useQuery } from '@apollo/client';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import { MenuItem, MenuSection, MenuSeparator } from '../../components/Menu';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import { UserType } from '../../graphql/generated/graphql';
import editIcon from '../../icons/non-prefilled/16x16/edit.svg';
import sidebarAddIcon from '../../icons/non-prefilled/16x16/sidebarAdd.svg';
import sidebarRemoveIcon from '../../icons/non-prefilled/16x16/sidebarRemove.svg';
import SidebarSectionIcon from '../../sidebar/SidebarSectionIcon';
import {
  PremadeSections,
  canEditSection,
  useUpdateSidebarSection,
} from '../../sidebar/SidebarSectionsProvider';
import useCanManageSidebarSections from '../../sidebar/useCanManageSidebarSections';
import { UIActions } from '../../ui/uiSlice';
import * as styles from './SidebarMenuItem.module.scss';

const ITEM_FRAGMENT = gql(`
  fragment SidebarMenuItem_item on SectionItem {
    ... on Post {
      id
    }
    ... on Topic {
      id
    }
  }
`);

const SIDEBAR_MENU_ITEM_QUERY = gql(/** @complexity 18 */ `
  query SidebarMenuItem {
    currentUser {
      sections {
        id
        name
        items {
          ...SidebarMenuItem_item
        }
        ...SidebarSectionIcon_section
      }
    }
    currentSession {
      user {
        id
        settings {
          sidebar {
            sectionIds
          }
        }
      }
      organization {
        id
        sections {
          id
          name
          items {
            ...SidebarMenuItem_item
          }
          ...SidebarSectionIcon_section
        }
      }
    }
  }
`);

interface SidebarMenuItemProps {
  item: DocumentType<typeof ITEM_FRAGMENT>;
}

const getItemText = (type: string): string => {
  switch (type) {
    case 'INLINE-ADD':
      return 'Add to sidebar';
    case 'INLINE-REMOVE':
      return 'Remove from sidebar';
    default:
      return 'Sidebar';
  }
};

const SidebarMenuItem = ({ item }: SidebarMenuItemProps) => {
  const dispatch = useAppDispatch();
  const { data } = useQuery(SIDEBAR_MENU_ITEM_QUERY);
  const [updateSidebarSection] = useUpdateSidebarSection();
  const isAdmin = useAppSelector(
    (state) => state.session.user.type === UserType.Admin,
  );
  const canManageSidebarSections = useCanManageSidebarSections();

  const collapsedSidebarSections = useAppSelector(
    (state) => state.ui.collapsedSidebarSections,
  );

  const teamSections = _.keyBy(
    data?.currentSession.organization.sections ?? [],
    ({ id }) => id,
  );

  const personalSections = _.keyBy(
    data?.currentUser.sections ?? [],
    ({ id }) => id,
  );

  const visibleSectionIds =
    data?.currentSession.user.settings.sidebar?.sectionIds ?? [];

  const visibleCustomSections = visibleSectionIds
    .map((sectionId) => teamSections[sectionId] ?? personalSections[sectionId])
    .filter((section) => !!section);

  const editableSections = visibleCustomSections.filter((section) =>
    canEditSection(section.id, {
      isTeamSection: !!teamSections[section.id],
      isAdminUser: isAdmin,
    }),
  );

  const [removeTargetSections, addTargetSections] = _.partition(
    editableSections,
    (section) => section.items.find(({ id }) => id === item.id),
  );

  const editablePersonalSections = editableSections.filter(
    (section) => !!personalSections[section.id],
  );

  const menuOptions =
    editablePersonalSections.length === 1 && editablePersonalSections[0]
      ? addTargetSections.includes(editablePersonalSections[0])
        ? { type: 'INLINE-ADD' as const, item: editablePersonalSections[0] }
        : { type: 'INLINE-REMOVE' as const, item: editablePersonalSections[0] }
      : { type: 'FULL-MENU' as const };

  type EditableSection = (typeof editableSections)[number];

  const addToSection = async (section: EditableSection) => {
    const isCollapsed = !!collapsedSidebarSections[section.id];

    if (isCollapsed) {
      dispatch(
        UIActions.toggleSidebarSection({
          section: section.id,
          isInitiallyCollapsed: false,
        }),
      );
    }

    await updateSidebarSection({
      variables: {
        id: section.id,
        itemIds: [...section.items.map(({ id }) => id), item.id],
      },
    });
  };

  const removeFromSection = async (section: EditableSection) => {
    await updateSidebarSection({
      variables: {
        id: section.id,
        itemIds: section.items
          .map(({ id }) => id)
          .filter((id) => id !== item.id),
      },
    });
  };

  const onMainMenuItemClicked = async () => {
    if (menuOptions.type === 'INLINE-ADD') {
      await addToSection(menuOptions.item);
    } else if (menuOptions.type === 'INLINE-REMOVE') {
      await removeFromSection(menuOptions.item);
    }
  };

  // FULL-MENU offers the possibility to manage sections, so we can only
  // allow INLINE-ADD and INLINE_REMOVE if `canManageSidebarSections` is false
  if (menuOptions.type === 'FULL-MENU' && !canManageSidebarSections) {
    return null;
  }

  return (
    <MenuItem
      icon={
        menuOptions.type === 'INLINE-REMOVE'
          ? sidebarRemoveIcon
          : sidebarAddIcon
      }
      onClick={onMainMenuItemClicked}
      submenuItems={
        menuOptions.type === 'FULL-MENU' && (
          <>
            {addTargetSections.length > 0 && (
              <MenuSection header="Add to">
                {addTargetSections.map((section) => {
                  return (
                    <MenuItem
                      key={section.id}
                      icon={<SidebarSectionIcon section={section} />}
                      onClick={() => addToSection(section)}
                    >
                      <div className={styles.sectionName}>{section.name}</div>
                    </MenuItem>
                  );
                })}
              </MenuSection>
            )}

            {removeTargetSections.length > 0 && (
              <MenuSection header="Remove from">
                {removeTargetSections.map((section) => {
                  return (
                    <MenuItem
                      key={section.id}
                      icon={<SidebarSectionIcon section={section} />}
                      onClick={() => removeFromSection(section)}
                    >
                      <div className={styles.sectionName}>{section.name}</div>
                    </MenuItem>
                  );
                })}
              </MenuSection>
            )}

            <MenuSeparator />

            <MenuItem
              onClick={() => {
                dispatch(
                  UIActions.setSelectedSectionId(
                    visibleSectionIds.at(0) ?? PremadeSections.MY_TOPICS,
                  ),
                );
              }}
              icon={editIcon}
            >
              Manage sections
            </MenuItem>
          </>
        )
      }
    >
      {getItemText(menuOptions.type)}
    </MenuItem>
  );
};

export default SidebarMenuItem;
